import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Alert, Box, Button, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { getPeople } from '@/features/people/api/people';
import { getRouteStopsForRange } from '@/features/scheduling/api/scheduling';
const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export function CalendarPage() {
  const now = new Date();
  const [month, setMonth] = useState(new Date(now.getFullYear(), now.getMonth(), 1));
  const [technicianId, setTechnicianId] = useState(0);
  const technicians = useQuery({
    queryKey: ['people', 'technicians'],
    queryFn: () => getPeople('technicians'),
  });
  const selectedTechnicianId = technicianId || technicians.data?.[0]?.id || 0;
  const start = new Date(month.getFullYear(), month.getMonth(), 1);
  const end = new Date(month.getFullYear(), month.getMonth() + 1, 0);
  const stops = useQuery({
    queryKey: ['calendar', selectedTechnicianId, iso(start)],
    queryFn: () => getRouteStopsForRange(selectedTechnicianId, iso(start), iso(end)),
    enabled: selectedTechnicianId > 0,
  });
  const cells = useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const last = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    const values: (Date | null)[] = Array.from({ length: (first.getDay() + 6) % 7 }, () => null);
    for (let i = 1; i <= last.getDate(); i++)
      values.push(new Date(month.getFullYear(), month.getMonth(), i));
    while (values.length % 7) values.push(null);
    return values;
  }, [month]);
  return (
    <Box sx={{ p: 1 }}>
      <Paper elevation={0} sx={paperSx}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          gap={2}
          sx={{ p: 2 }}
        >
          <Box>
            <Typography variant="h1">Technician Calendar</Typography>
            <Typography color="text.secondary">
              Scheduled service visits from the existing route engine.
            </Typography>
          </Box>
          <Stack direction="row" gap={1} alignItems="center">
            <TextField
              select
              size="small"
              label="Technician"
              value={selectedTechnicianId}
              onChange={(e) => setTechnicianId(Number(e.target.value))}
              sx={{ minWidth: 220 }}
            >
              {(technicians.data ?? []).map((x) => (
                <MenuItem key={x.id} value={x.id}>
                  {x.name}
                </MenuItem>
              ))}
            </TextField>
            <Button
              aria-label="Previous month"
              onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
            >
              <ChevronLeftIcon />
            </Button>
            <Typography minWidth={130} textAlign="center" fontWeight={600}>
              {month.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
            </Typography>
            <Button
              aria-label="Next month"
              onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
            >
              <ChevronRightIcon />
            </Button>
          </Stack>
        </Stack>
      </Paper>
      {stops.isError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          The schedule could not be loaded.
        </Alert>
      )}
      <Paper elevation={0} sx={{ ...paperSx, mt: 1.5, overflowX: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7,minmax(130px,1fr))',
            minWidth: 910,
          }}
        >
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((x) => (
            <Typography
              key={x}
              fontWeight={600}
              sx={{ p: 1, bgcolor: '#f3f2f1', borderBottom: '1px solid #e1dfdd' }}
            >
              {x}
            </Typography>
          ))}
          {cells.map((date, i) => (
            <Box
              key={i}
              sx={{
                minHeight: 120,
                p: 1,
                borderRight: '1px solid #edebe9',
                borderBottom: '1px solid #edebe9',
                bgcolor: date && [0, 6].includes(date.getDay()) ? '#f8fbff' : 'white',
              }}
            >
              {date && (
                <>
                  <Typography variant="body2" fontWeight={600}>
                    {date.getDate()}
                  </Typography>
                  {(stops.data?.[iso(date)] ?? []).map((stop) => (
                    <Button
                      key={stop.id}
                      component={Link}
                      to={`/clients/${stop.service_location.client.id}`}
                      fullWidth
                      sx={{
                        mt: 0.5,
                        p: 0.75,
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        bgcolor: stop.cancellation ? '#fde7e9' : '#e6f2fb',
                        color: stop.cancellation ? 'error.main' : 'primary.main',
                        textTransform: 'none',
                      }}
                    >
                      {stop.service_location.client.name} ({stop.service_location.name})
                      {stop.cancellation ? ' — Cancelled' : ''}
                    </Button>
                  ))}
                </>
              )}
            </Box>
          ))}
        </Box>
      </Paper>
    </Box>
  );
}
const paperSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  boxShadow: '0 2px 6px rgba(0,0,0,.12)',
};
